import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Send, X, Sparkles, ExternalLink } from "lucide-react";
import { aiChat, aiSessionId, euro, waLink, track, AVAILABILITY_META } from "@/lib/store";

const SUGGESTIONS = [
  "Quanto costa l'iPhone 15 128GB nero?",
  "Avete il Samsung Galaxy S24?",
  "Quanto costa cambiare il display dell'iPhone 13?",
  "Posso venire venerdì pomeriggio?",
];

const ProductCard = ({ c }) => {
  const meta = AVAILABILITY_META[c.availability] || AVAILABILITY_META.DA_VERIFICARE;
  const specs = [
    (c.storages || []).join(" · "),
    (c.colors || []).join(", "),
    c.condition && c.condition !== "NUOVO" ? (c.grade ? `${c.condition} ${c.grade}` : c.condition) : null,
    c.warranty_months ? `Garanzia ${c.warranty_months} mesi` : null,
  ].filter(Boolean).join(" • ");
  return (
    <div data-testid="ai-product-card" className="mt-2 overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex gap-3 p-2.5">
        {c.image && <img src={c.image} alt={c.name} className="h-16 w-16 flex-shrink-0 rounded-lg object-contain" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-neutral-900">{c.name}</p>
          <p className="mt-0.5 text-sm font-bold text-black">{c.price_cents ? euro(c.price_cents) : "Da verificare"}</p>
          {specs && <p className="mt-0.5 line-clamp-2 text-[11px] leading-tight text-neutral-500">{specs}</p>}
          <span className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${meta.cls}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />{meta.label}
          </span>
        </div>
      </div>
      <div className="flex border-t border-neutral-200 text-xs">
        <a href={waLink(`Ciao, sono interessato a ${c.name}`)} target="_blank" rel="noopener noreferrer" className="flex-1 py-2 text-center font-medium text-[#128C4A] hover:bg-neutral-50">Chiedi su WhatsApp</a>
      </div>
    </div>
  );
};

const ActionButton = ({ a }) => (
  <a data-testid="ai-action-btn" href={a.url} target="_blank" rel="noopener noreferrer"
    className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-3 py-2 text-xs font-semibold text-white hover:bg-[#20ba59]">
    {a.label}
  </a>
);

export const AiAssistant = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Ciao! Sono l'assistente di iPhix Scafati 👋 Dimmi il modello che ti interessa (es. «iPhone 15 128GB nero») e ti dico prezzo e disponibilità. Posso anche darti un preventivo di riparazione.", cards: [] }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(aiSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open, busy]);

  useEffect(() => {
    const openFn = () => { setOpen(true); track("ai_open"); };
    window.addEventListener("iphix:open-ai", openFn);
    return () => window.removeEventListener("iphix:open-ai", openFn);
  }, []);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: content, cards: [] }]);
    setBusy(true);
    try {
      const data = await aiChat(sid.current, content);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, cards: data.cards || [], actions: data.actions || [] }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Ops, qualcosa è andato storto. Riprova o scrivici su WhatsApp.", cards: [] }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button data-testid="ai-open-btn" onClick={() => { setOpen(true); track("ai_open"); }}
          className="fixed bottom-24 right-5 z-40 flex items-center gap-2 rounded-full bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-2xl shadow-blue-500/30 ring-1 ring-blue-900/10 transition-transform hover:scale-105 active:scale-95">
          <Sparkles className="h-5 w-5" /> Chiedi prezzi
        </button>
      )}

      {open && (
        <div data-testid="ai-panel" className="fixed inset-x-3 bottom-3 z-50 flex h-[80vh] flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl sm:inset-x-auto sm:bottom-5 sm:right-5 sm:h-[600px] sm:w-[400px]">
          <header className="flex items-center gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600"><Bot className="h-5 w-5 text-white" /></span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-neutral-900">Assistente iPhix</p>
              <p className="text-[11px] text-emerald-600">● Online · assistenza IPHIX</p>
            </div>
            <button data-testid="ai-close-btn" onClick={() => setOpen(false)} className="ml-auto rounded-full p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-black"><X className="h-5 w-5" /></button>
          </header>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-neutral-50/50 px-3 py-4">
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
                  <div data-testid={`ai-msg-${m.role}`} className={`whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-blue-600 text-white" : "bg-neutral-100 text-neutral-800"}`}>{m.text}</div>
                  {(m.cards || []).map((c, j) => <ProductCard key={j} c={c} />)}
                  {(m.actions || []).map((a, j) => <ActionButton key={`a${j}`} a={a} />)}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="flex gap-1 rounded-2xl bg-neutral-100 px-4 py-3"><span className="h-2 w-2 animate-bounce rounded-full bg-neutral-400" style={{ animationDelay: "0ms" }} /><span className="h-2 w-2 animate-bounce rounded-full bg-neutral-400" style={{ animationDelay: "150ms" }} /><span className="h-2 w-2 animate-bounce rounded-full bg-neutral-400" style={{ animationDelay: "300ms" }} /></div></div>}

            {msgs.length === 1 && !busy && (
              <div className="space-y-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="ai-suggestion" onClick={() => send(s)} className="flex w-full items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-left text-xs text-neutral-700 transition-colors hover:border-black hover:text-black">
                    <ExternalLink className="h-3.5 w-3.5 text-black" />{s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-neutral-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea data-testid="ai-input" rows={1} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Scrivi un messaggio…"
                className="max-h-24 flex-1 resize-none rounded-xl border border-neutral-300 bg-white px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-black focus:outline-none" />
              <button data-testid="ai-send-btn" onClick={() => send()} disabled={busy || !input.trim()} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition-opacity disabled:opacity-40"><Send className="h-4 w-4" /></button>
            </div>
            <p className="mt-1.5 text-center text-[10px] text-neutral-400">L'assistente legge solo dati reali del negozio. Il prezzo può variare in base a modifiche del fornitore.</p>
          </div>
        </div>
      )}
    </>
  );
};
